import Vue from 'vue'
import { months } from '@/lib/kpi-data'
import {
  listKpis,
  updateRealizado as apiUpdateRealizado,
  updateRealizadoBulk as apiUpdateRealizadoBulk,
  updateMetaMonth as apiUpdateMetaMonth,
} from '@/services/kpis'

function humanizeKpiKey(key) {
  // Ex: "conversaoAHA" -> "Conversao AHA"
  const s = String(key || '')
  if (!s) return ''
  const spaced = s.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Rótulos amigáveis por setor/kpi (exibição no dashboard)
const KPI_LABELS_BY_SECTOR = {
  geral: {
    producao: 'Produção',
  },
  comercial: {
    meta_geral: '1 - Meta Geral',
    vendas: 'Vendas',
    reunioes: 'Reuniões',
    conversao: 'Conversão',
    '2 - meta_b2c': '2 - Meta B2C',
    '3 - meta_b2b': '3 - Meta B2B',
    '4 - meta_lideres': '4 - Meta Líderes',
    lider_local_b2c: 'Líder Local B2C',
    lider_local_b2b: 'Líder Local B2B',
    meta_b2b_is: 'Meta B2B IS',
    vendas_leads_qualificados: 'Leads (Vendas leads qualificados)',
  },
  marketing: {
    marketing: 'Marketing',
    cac: 'CAC',
    cpl: 'CPL',
    investimento: 'Investimento',
    ecommerce: 'E-commerce',
  },
  compras: {
    aquisicoes: 'Aquisições',
    orcado: 'Orçado',
    qtd_reservas_diarias_salas: 'Quantidade de reservas de diárias de salas',
    qtd_arquisicao_coffee: 'Quantidade de aquisição de kits Coffee',
    qtd_reservas_hospedagem: 'Quantidade de reservas de hospedagem',
  },
  ti: {
    prejuizo: 'Prejuízo',
  },
  logistica: {
    aereas: '1 - Aéreas',
    fretes: '2 - Fretes',
    instrutores: '3 - Quantidade de turmas atendidas com instrutores locais',
    turmas: '4 - Antecedência na compra de passagens',
    turmas_problemas: '5 - Turmas com problemas',
  },
  academico: {
    certificados: '1 - Certificados',
    inscricoes: '2 - Inscrições',
    turmasREC: '3 - Turmas REC',
    // conversao: 'Conversão',
    prospeccao_aha: '4 - Quantidade prospecção AHA',
    qtd_links_enviados: '5 - Quantidade de links enviados',
    conversao_links_vendas: '6 - Conversão de links em vendas efetivas',

  },
}

function emptyMonthMap() {
  const m = {}
  months.forEach((month) => {
    m[month] = { meta: 0, realizado: 0, format: undefined }
  })
  return m
}

export default {
  namespaced: true,
  state: () => ({
    // data[sector][kpiKey][month] = { meta, realizado, format } — agregado mensal (soma das 4 semanas)
    data: {},
    // dataWeekly[sector][kpiKey][month][week] = { meta, realizado } — por semana (1–4)
    dataWeekly: {},
    defsBySector: {},
    isLoading: false,
  }),
  getters: {
    getData: (state) => (sector, kpiKey, month) => state.data?.[sector]?.[kpiKey]?.[month],
    getWeeklyData: (state) => (sector, kpiKey, month, week) =>
      state.dataWeekly?.[sector]?.[kpiKey]?.[month]?.[String(week)],
    getDefsForSector: (state) => (sector) => state.defsBySector?.[sector] || [],
  },
  mutations: {
    SET_LOADING(state, value) {
      state.isLoading = Boolean(value)
    },
    CLEAR_SECTOR(state, sector) {
      if (sector != null && sector !== '') {
        Vue.set(state.data, sector, {})
        Vue.set(state.dataWeekly, sector, {})
      }
    },
    APPLY_ROW(state, row) {
      const sector = String(row.sector)
      const kpiKey = String(row.kpi_name)
      const month = String(row.month)
      const week = row.week != null ? Number(row.week) : null
      const meta = row.meta == null ? undefined : Number(row.meta)
      const realizado = row.realizado == null ? undefined : Number(row.realizado)
      const format = row.format == null ? undefined : String(row.format)

      if (!state.data[sector]) Vue.set(state.data, sector, {})
      if (!state.data[sector][kpiKey]) Vue.set(state.data[sector], kpiKey, emptyMonthMap())
      const cur = state.data[sector][kpiKey][month] || { meta: 0, realizado: 0, format: undefined }

      if (week != null && !Number.isNaN(week)) {
        // Dados por semana: guardar em dataWeekly e somar em data[month] (exibição mensal = soma das 4 semanas)
        if (!state.dataWeekly[sector]) Vue.set(state.dataWeekly, sector, {})
        if (!state.dataWeekly[sector][kpiKey]) Vue.set(state.dataWeekly[sector], kpiKey, {})
        if (!state.dataWeekly[sector][kpiKey][month]) Vue.set(state.dataWeekly[sector][kpiKey], month, {})
        Vue.set(state.dataWeekly[sector][kpiKey][month], String(week), {
          meta: meta != null && !Number.isNaN(meta) ? meta : 0,
          realizado: realizado != null && !Number.isNaN(realizado) ? realizado : 0,
        })
        Vue.set(state.data[sector][kpiKey], month, {
          meta: (cur.meta || 0) + (meta != null && !Number.isNaN(meta) ? meta : 0),
          realizado: (cur.realizado || 0) + (realizado != null && !Number.isNaN(realizado) ? realizado : 0),
          format: format || cur.format,
        })
      } else {
        // Linha sem week (ex.: Meta Geral): sobrescreve o mês
        Vue.set(state.data[sector][kpiKey], month, {
          meta: meta != null && !Number.isNaN(meta) ? meta : cur.meta,
          realizado: realizado != null && !Number.isNaN(realizado) ? realizado : cur.realizado,
          format: format || cur.format,
        })
      }
    },
    SET_REALIZADO(state, { sector, kpiKey, month, value }) {
      if (!state.data[sector]) Vue.set(state.data, sector, {})
      if (!state.data[sector][kpiKey]) Vue.set(state.data[sector], kpiKey, emptyMonthMap())
      const existing = state.data[sector][kpiKey][month] || { meta: 0, realizado: 0, format: undefined }
      Vue.set(state.data[sector][kpiKey], month, { ...existing, realizado: Number(value) })
    },
    REBUILD_DEFS(state) {
      const defs = {}

      Object.keys(state.data || {}).forEach((sector) => {
        const kpis = state.data[sector] || {}
        const sectorLabels = KPI_LABELS_BY_SECTOR[sector] || {}
        const list = Object.keys(kpis).map((kpiKey) => {
          // pega format de qualquer mês que tiver
          let fmt
          months.some((m) => {
            const v = kpis?.[kpiKey]?.[m]
            if (v?.format) {
              fmt = v.format
              return true
            }
            return false
          })

          const label = sectorLabels[kpiKey] || humanizeKpiKey(kpiKey)

          return {
            key: kpiKey,
            name: label,
            format: fmt || 'number',
          }
        })

        // ordena pelo nome para ficar estável
        list.sort((a, b) => String(a.name).localeCompare(String(b.name), 'pt-BR'))
        defs[sector] = list
      })

      state.defsBySector = defs
    },
  },
  actions: {
    async fetchAll({ commit }) {
      commit('SET_LOADING', true)
      try {
        const rows = await listKpis()
        if (Array.isArray(rows)) {
          rows.forEach((r) => commit('APPLY_ROW', r))
          commit('REBUILD_DEFS')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[dashboard-vue] Error loading KPIs:', e)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchSector({ commit }, { sector, year } = {}) {
      if (!sector) return
      commit('SET_LOADING', true)
      try {
        commit('CLEAR_SECTOR', sector)
        const rows = await listKpis({ sector, year })
        if (Array.isArray(rows)) {
          rows.forEach((r) => commit('APPLY_ROW', r))
          commit('REBUILD_DEFS')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[dashboard-vue] Error loading KPIs (sector fetch):', e)
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async updateRealizado({ commit }, { sector, kpiKey, month, value }) {
      // Atualiza otimista (mantém UX do React)
      commit('SET_REALIZADO', { sector, kpiKey, month, value })
      try {
        const row = await apiUpdateRealizado({
          sector,
          kpi_name: kpiKey,
          month,
          realizado: value,
        })
        if (row) commit('APPLY_ROW', row)

        // Refetch do setor para manter lista consistente após edição
        if (sector === 'comercial') {
          await this.dispatch('kpis/fetchSector', { sector: 'comercial' })
        }
      } catch (e) {
        // Se falhar, mantém o valor local (pode ser ajustado depois com feedback/notify)
        // eslint-disable-next-line no-console
        console.error('[dashboard-vue] Error updating KPI:', e)
      }
    },

    /**
     * Atualiza vários meses de uma vez (1 request).
     * Mantém fallback automático pro update individual caso a API ainda não suporte o bulk.
     */
    async updateRealizadoBulk({ commit, dispatch }, { sector, kpiKey, items }) {
      const normalized = (Array.isArray(items) ? items : [])
        .map((i) => ({
          sector,
          kpiKey,
          month: i?.month,
          week: i?.week,
          value: i?.value,
        }))
        .filter((i) => i.month && i.value != null && !Number.isNaN(Number(i.value)))

      const isWeekly = normalized.some((i) => i.week != null)
      if (!isWeekly) {
        normalized.forEach((i) => {
          commit('SET_REALIZADO', { sector: i.sector, kpiKey: i.kpiKey, month: i.month, value: i.value })
        })
      }

      if (normalized.length === 0) return

      try {
        const bulkItems = []
        if (isWeekly) {
          normalized.forEach((i) => {
            bulkItems.push({
              sector: i.sector,
              kpi_name: i.kpiKey,
              month: i.month,
              week: Number(i.week),
              realizado: Number(i.value),
            })
          })
        } else {
          normalized.forEach((i) => {
            const value = Number(i.value)
            const valuePerWeek = Math.round((value / 4) * 10000) / 10000
            for (let week = 1; week <= 4; week++) {
              bulkItems.push({
                sector: i.sector,
                kpi_name: i.kpiKey,
                month: i.month,
                week,
                realizado: valuePerWeek,
              })
            }
          })
        }

        await apiUpdateRealizadoBulk({ items: bulkItems })
        // O componente que chamou o bulk faz refetch do setor ao fechar o modal,
        // assim todos os setores (incl. comercial/Meta Geral) exibem dados atualizados.
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[dashboard-vue] Bulk KPI update failed, falling back to single updates:', e)
        if (isWeekly) {
          for (const i of normalized) {
            try {
              const row = await apiUpdateRealizado({
                sector: i.sector,
                kpi_name: i.kpiKey,
                month: i.month,
                week: i.week,
                realizado: Number(i.value),
              })
              if (row) commit('APPLY_ROW', row)
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[dashboard-vue] Single week update failed:', err)
            }
          }
        } else {
          const valuePerWeekByMonth = {}
          normalized.forEach((i) => {
            valuePerWeekByMonth[i.month] = Math.round((Number(i.value) / 4) * 10000) / 10000
          })
          for (const i of normalized) {
            for (let week = 1; week <= 4; week++) {
              try {
                const row = await apiUpdateRealizado({
                  sector: i.sector,
                  kpi_name: i.kpiKey,
                  month: i.month,
                  week,
                  realizado: valuePerWeekByMonth[i.month],
                })
                if (row) commit('APPLY_ROW', row)
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[dashboard-vue] Single week update failed:', err)
              }
            }
          }
        }
        if (shouldRefreshComercialMetaGeral) {
          await dispatch('fetchSector', { sector: 'comercial' })
        }
      }
    },

    /**
     * Atualiza a meta de um mês: divide o valor pelas 4 semanas e persiste por semana na API.
     */
    async updateMeta({ commit, dispatch }, { sector, kpiKey, month, meta, year }) {
      const metaNum = Number(meta)
      if (metaNum == null || Number.isNaN(metaNum)) return
      try {
        const rows = await apiUpdateMetaMonth({
          year: year || new Date().getFullYear(),
          sector,
          kpi_name: kpiKey,
          month,
          meta: metaNum,
        })
        if (Array.isArray(rows)) rows.forEach((r) => commit('APPLY_ROW', r))
        if (sector === 'comercial') {
          await dispatch('fetchSector', { sector: 'comercial' })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[dashboard-vue] Error updating meta:', e)
        throw e
      }
    },
  },
}

